import { active_vm_frame } from './activeVM';

// ---------------------------------------------------------------------------
// Injection queue — serialises all VDI injections so they never race.
// ---------------------------------------------------------------------------
let _injectChain = Promise.resolve();

/**
 * Injects transcribed text into the currently focused element.
 *
 * Strategy:
 *   1. Focused element is an <input>, <textarea>, or contenteditable
 *      in the same origin → insert text at the cursor directly.
 *   2. VDI iframe is active → send keystrokes directly via the Guacamole
 *      client API (sendKeyEvent), bypassing the clipboard sync entirely.
 *   3. Fallback → clipboard + toast "press Ctrl+V".
 */
export function injectText(text) {
  if (!text) return;

  const el = document.activeElement;

  if (isDirectlyInjectable(el)) {
    injectIntoElement(el, text);
    return;
  }

  // Queue VDI injections so concurrent dictations never overlap.
  _injectChain = _injectChain
    .then(() => injectIntoVDI(text))
    .catch(() => {
      // Wrap showToast so that a DOM error (e.g. body not ready) can't break
      // the chain permanently — the next dictation would otherwise skip VDI
      // injection and always land in this catch with the wrong text.
      try { showToast(`Dictated: "${text}"`); } catch (_) {}
    });
}

// ---------------------------------------------------------------------------
// VDI injection
// ---------------------------------------------------------------------------

async function injectIntoVDI(text) {
  // Always keep clipboard fresh so manual Ctrl+V works as a fallback,
  // regardless of which injection path succeeds.
  navigator.clipboard.writeText(text).catch(() => {});

  // Try to type directly into VDI via Guacamole API.
  tryGuacamoleInject(text);
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  showToast(`${words} word${words === 1 ? '' : 's'} — press Ctrl+V to paste`);
}

/**
 * Sends text to the remote VM by calling Guacamole.Client.sendKeyEvent()
 * for each character.  The keysym for printable ASCII equals its Unicode
 * code point, so no lookup table is required.
 *
 * Returns true if the Guacamole client was reachable, false otherwise.
 */
function tryGuacamoleInject(text) {
  const iframe = active_vm_frame.get();
  if (!iframe) return false;

  try {
    // Path discovered from resize.js: angular.element('.display').scope().client.client
    // .client (outer) = Guacamole.ManagedClient
    // .client (inner) = Guacamole.Client  — has sendKeyEvent()
    const guacClient = iframe.contentWindow?.angular
      ?.element('.display')
      ?.scope()
      ?.client
      ?.client;

    if (!guacClient || typeof guacClient.sendKeyEvent !== 'function') return false;

    for (const char of text) {
      const keysym = char.charCodeAt(0);
      if (keysym < 0x20 || keysym > 0x7E) continue; // printable ASCII only
      guacClient.sendKeyEvent(1, keysym); // key down
      guacClient.sendKeyEvent(0, keysym); // key up
    }

    return true;
  } catch (e) {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Same-origin direct injection
// ---------------------------------------------------------------------------

function isDirectlyInjectable(el) {
  if (!el || el === document.body || el === document.documentElement) return false;

  const tag = el.tagName.toLowerCase();
  if (tag === 'input') {
    if (el.type === 'password') return false; // never inject into password fields
    return true;
  }
  if (tag === 'textarea') return true;
  if (el.isContentEditable) return true;

  return false;
}

function injectIntoElement(el, text) {
  const tag = el.tagName.toLowerCase();

  if (tag === 'input' || tag === 'textarea') {
    const start = el.selectionStart ?? el.value.length;
    const end   = el.selectionEnd   ?? el.value.length;

    const before = el.value.slice(0, start);
    const after  = el.value.slice(end);
    const prefix = before.length > 0 && !before.endsWith(' ') ? ' ' : '';
    const inserted = prefix + text;

    el.value = before + inserted + after;

    const cursor = start + inserted.length;
    el.setSelectionRange(cursor, cursor);

    el.dispatchEvent(new Event('input',  { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
    return;
  }

  if (el.isContentEditable) {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      selection.deleteFromDocument();
      const range = selection.getRangeAt(0);
      const node  = document.createTextNode(text + ' ');
      range.insertNode(node);
      range.setStartAfter(node);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      el.textContent += text + ' ';
    }
    el.dispatchEvent(new Event('input', { bubbles: true }));
  }
}

// ---------------------------------------------------------------------------
// Chime (synthesised — no audio files needed)
// ---------------------------------------------------------------------------

function playChime() {
  try {
    const ctx = new AudioContext();
    const master = ctx.createGain();
    master.gain.value = 0.25;
    master.connect(ctx.destination);

    const note = (freq, start, dur) => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(1, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
      osc.connect(gain);
      gain.connect(master);
      osc.start(start);
      osc.stop(start + dur);
    };

    const t = ctx.currentTime;
    note(523,  t,        0.12); // C5
    note(1568, t + 0.08, 0.12); // G6

    setTimeout(() => ctx.close(), 300);
  } catch (e) { /* AudioContext unavailable — silent fallback */ }
}

// ---------------------------------------------------------------------------
// Toast notification (self-contained, no dependencies)
// ---------------------------------------------------------------------------

let _toastEl = null;
let _toastTimer = null;

function showToast(message) {
  if (!_toastEl) {
    _toastEl = document.createElement('div');
    Object.assign(_toastEl.style, {
      position:        'fixed',
      bottom:          '90px',
      left:            '50%',
      transform:       'translateX(-50%)',
      background:      'rgba(0, 0, 0, 0.82)',
      color:           '#fff',
      padding:         '10px 22px',
      borderRadius:    '6px',
      fontSize:        '14px',
      fontFamily:      'sans-serif',
      zIndex:          '2147483647',
      pointerEvents:   'none',
      whiteSpace:      'nowrap',
      boxShadow:       '0 4px 12px rgba(0,0,0,0.35)',
      transition:      'opacity 0.3s ease',
      opacity:         '0',
    });
    document.body.appendChild(_toastEl);
  }

  _toastEl.textContent = message;
  _toastEl.style.opacity = '1';
  playChime();

  clearTimeout(_toastTimer);
  _toastTimer = setTimeout(() => {
    if (_toastEl) _toastEl.style.opacity = '0';
  }, 3500);
}
